import isEmpty from 'lodash/isEmpty';
import { clearCurrentUser, fetchCurrentUser } from './user.duck';
import { storableError } from '../util/errors';
import * as log from '../util/log';
import { addOrUpdateAPerson } from '../custom-ducks/pipedrive.duck';
import { sendOnboardingWelcomeEmail } from '../custom-ducks/marketing.duck';
import { endCallAndCleanup } from '../custom-ducks/webrtc.duck';
import {
  buildDefaultAvailabilityData,
  saveCalendarAvailabilitySetting,
  saveParticipantCalendarAvailabilitySetting,
  setPendingDefaultAvailabilitySave,
} from '../custom-ducks/settings.duck';
import config from '../config';

const authenticated = authInfo => authInfo && authInfo.grantType === 'refresh_token';

// Silently sync auth state with V-Corp via a hidden iframe instead of a
// visible new tab. window.open(url, '_blank') — even paired with
// popup.blur()/window.focus() — still switched the active tab to V-Corp on
// every login/logout in practice: browsers largely ignore script-driven
// focus changes once you're a tick past the original click (inside a
// promise .then(), there's no "user activation" left to justify it). An
// iframe never creates a tab in the first place, so there's nothing to
// switch away to.
// `sandbox="allow-scripts allow-same-origin"` lets V-Corp's app run and set
// its own cookies/localStorage (needed for the auth sync to actually work)
// but — deliberately missing allow-top-navigation — blocks it from
// navigating this (parent) tab away to vcorp.viveka.world if its code ever
// tries to bust out of an iframe.
//
// Returns a promise that resolves once the sync has had a chance to land.
// This matters for organization users: after login, LoginPage does a full
// window.location.href navigation to V-Corp as soon as currentUser loads.
// That navigation unloads this tab and kills the iframe's in-flight
// request — if it fires before the iframe's own token exchange completes,
// V-Corp never gets its cookie and the org user lands there logged out,
// forcing a second manual login. Callers that lead into that redirect
// (see login() below) must await this before letting currentUser resolve.
const syncVCorpAuthInBackground = url => {
  if (typeof document === 'undefined') return Promise.resolve();
  return new Promise(resolve => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve();
    };

    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin');
    iframe.onload = finish;
    iframe.onerror = finish;
    iframe.src = url;
    document.body.appendChild(iframe);

    // The auto-auth page's own token exchange can finish shortly after
    // `onload` fires, not exactly at that instant — so don't resolve the
    // instant the iframe's document loads. Give it a floor of 1.5s, and a
    // ceiling of 4s in case V-Corp is slow/unreachable, so login never
    // hangs indefinitely on a background sync.
    setTimeout(finish, 1500);
    const hardTimeoutId = setTimeout(finish, 4000);

    // Clean up well after resolving so hidden iframes don't pile up across
    // repeated login/logout.
    setTimeout(() => {
      clearTimeout(hardTimeoutId);
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    }, 10000);
  });
};

// ================ Action types ================ //

export const AUTH_INFO_REQUEST = 'app/Auth/AUTH_INFO_REQUEST';
export const AUTH_INFO_SUCCESS = 'app/Auth/AUTH_INFO_SUCCESS';

export const LOGIN_REQUEST = 'app/Auth/LOGIN_REQUEST';
export const LOGIN_SUCCESS = 'app/Auth/LOGIN_SUCCESS';
export const LOGIN_ERROR = 'app/Auth/LOGIN_ERROR';

export const LOGOUT_REQUEST = 'app/Auth/LOGOUT_REQUEST';
export const LOGOUT_SUCCESS = 'app/Auth/LOGOUT_SUCCESS';
export const LOGOUT_ERROR = 'app/Auth/LOGOUT_ERROR';

export const SIGNUP_REQUEST = 'app/Auth/SIGNUP_REQUEST';
export const SIGNUP_SUCCESS = 'app/Auth/SIGNUP_SUCCESS';
export const SIGNUP_ERROR = 'app/Auth/SIGNUP_ERROR';

// IdP (Facebook / Google / Apple) signup — only the "create user
// from a pending IdP token" branch runs through Redux. The OAuth
// login path itself is handled server-side and just sets the
// Sharetribe auth cookie; the frontend reacts via authInfo() like
// any other authenticated session.
export const SIGNUP_WITH_IDP_REQUEST = 'app/Auth/SIGNUP_WITH_IDP_REQUEST';
export const SIGNUP_WITH_IDP_SUCCESS = 'app/Auth/SIGNUP_WITH_IDP_SUCCESS';
export const SIGNUP_WITH_IDP_ERROR = 'app/Auth/SIGNUP_WITH_IDP_ERROR';

// Generic user_logout action that can be handled elsewhere
// E.g. src/reducers.js clears store as a consequence
export const USER_LOGOUT = 'app/USER_LOGOUT';

// ================ Reducer ================ //

const initialState = {
  isAuthenticated: false,

  // auth info
  authInfoLoaded: false,

  // login
  loginError: null,
  loginInProgress: false,

  // logout
  logoutError: null,
  logoutInProgress: false,

  // signup
  signupError: null,
  signupInProgress: false,

  userType: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case AUTH_INFO_REQUEST:
      return state;
    case AUTH_INFO_SUCCESS:
      return { ...state, authInfoLoaded: true, isAuthenticated: authenticated(payload) };

    case LOGIN_REQUEST:
      return {
        ...state,
        loginInProgress: true,
        loginError: null,
        logoutError: null,
        signupError: null,
      };
    case LOGIN_SUCCESS:
      return { ...state, loginInProgress: false, isAuthenticated: true };
    case LOGIN_ERROR:
      return { ...state, loginInProgress: false, loginError: payload };

    case LOGOUT_REQUEST:
      return { ...state, logoutInProgress: true, loginError: null, logoutError: null };
    case LOGOUT_SUCCESS:
      return { ...state, logoutInProgress: false, isAuthenticated: false };
    case LOGOUT_ERROR:
      return { ...state, logoutInProgress: false, logoutError: payload };

    case SIGNUP_REQUEST:
      return { ...state, signupInProgress: false, loginError: null, signupError: null };
    case SIGNUP_SUCCESS:
      return { ...state, signupInProgress: false, userType: payload };
    case SIGNUP_ERROR:
      return { ...state, signupInProgress: false, signupError: payload };

    // IdP signup reuses signupInProgress / signupError so the UI's
    // existing in-progress and error handling on SignUpPage works
    // without changes.
    case SIGNUP_WITH_IDP_REQUEST:
      return { ...state, signupInProgress: true, signupError: null, loginError: null };
    case SIGNUP_WITH_IDP_SUCCESS:
      return { ...state, signupInProgress: false, userType: payload, isAuthenticated: true };
    case SIGNUP_WITH_IDP_ERROR:
      return { ...state, signupInProgress: false, signupError: payload };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const authenticationInProgress = state => {
  const { loginInProgress, logoutInProgress, signupInProgress } = state.Auth;
  return loginInProgress || logoutInProgress || signupInProgress;
};

// ================ Action creators ================ //

export const authInfoRequest = () => ({ type: AUTH_INFO_REQUEST });
export const authInfoSuccess = info => ({ type: AUTH_INFO_SUCCESS, payload: info });

export const loginRequest = () => ({ type: LOGIN_REQUEST });
export const loginSuccess = () => ({ type: LOGIN_SUCCESS });
export const loginError = error => ({ type: LOGIN_ERROR, payload: error, error: true });

export const logoutRequest = () => ({ type: LOGOUT_REQUEST });
export const logoutSuccess = () => ({ type: LOGOUT_SUCCESS });
export const logoutError = error => ({ type: LOGOUT_ERROR, payload: error, error: true });

export const signupRequest = () => ({ type: SIGNUP_REQUEST });
export const signupSuccess = type => ({ type: SIGNUP_SUCCESS, payload: type });
export const signupError = error => ({ type: SIGNUP_ERROR, payload: error, error: true });

export const signupWithIdpRequest = () => ({ type: SIGNUP_WITH_IDP_REQUEST });
export const signupWithIdpSuccess = type => ({ type: SIGNUP_WITH_IDP_SUCCESS, payload: type });
export const signupWithIdpError = error => ({
  type: SIGNUP_WITH_IDP_ERROR,
  payload: error,
  error: true,
});

export const userLogout = () => ({ type: USER_LOGOUT });

// ================ Thunks ================ //

export const authInfo = () => (dispatch, getState, sdk) => {
  dispatch(authInfoRequest());
  return sdk
    .authInfo()
    .then(info => dispatch(authInfoSuccess(info)))
    .catch(e => {
      // Requesting auth info just reads the token from the token
      // store (i.e. cookies), and should not fail in normal
      // circumstances. If it fails, it's due to a programming
      // error. In that case we mark the operation done and dispatch
      // `null` success action that marks the user as unauthenticated.
      log.error(e, 'auth-info-failed');
      dispatch(authInfoSuccess(null));
    });
};

export const login = (username, password) => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(loginRequest());

  // Note that the thunk does not reject when the login fails, it
  // just dispatches the login error action.
  return sdk
    .login({ username, password })
    .then(res => {
      dispatch(loginSuccess());
      return syncVCorpAuthInBackground(
        `${config.vCorpUrl}/auth/auto-auth/${res?.data?.access_token}/${res?.data?.refresh_token}`
      );
    })
    .then(() => dispatch(fetchCurrentUser()))
    .then(() => dispatch(addOrUpdateAPerson()))
    .catch(e => dispatch(loginError(storableError(e))));
};

export const logout = () => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }

  // End any active video call BEFORE we tear down auth/state. The USER_LOGOUT
  // root reducer wipes redux state but only orphans the live peer connection;
  // this stops media, releases the camera/mic, notifies the peer, and drops
  // the signaling socket so the call doesn't keep running after logout.
  dispatch(endCallAndCleanup());

  dispatch(logoutRequest());

  // Note that the thunk does not reject when the logout fails, it
  // just dispatches the logout error action.
  return sdk
    .logout()
    .then(() => {
      // The order of the dispatched actions
      dispatch(logoutSuccess());
      dispatch(clearCurrentUser());
      log.clearUserId();
      dispatch(userLogout());
      syncVCorpAuthInBackground(`${config.vCorpUrl}/auth/auto-auth-logout`);
    })
    .catch(e => dispatch(logoutError(storableError(e))));
};

export const signup = (params, type) => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(signupRequest());
  const { email, password, firstName, lastName } = params;

  const isProvider = type === 'iac' ? true : false;
  console.log({ type, isProvider });

  // Sharetribe defaults displayName to "FirstName L." (last initial only)
  // when it isn't passed explicitly. Set it here so both coach and
  // participant accounts show the full first + last name.
  const displayName = `${firstName} ${lastName}`;

  const createUserParams = isEmpty(type)
    ? { email, password, firstName, lastName, displayName }
    : { email, password, firstName, lastName, displayName, publicData: { isProvider } };

  // We must login the user if signup succeeds since the API doesn't
  // do that automatically.
  return sdk.currentUser
    .create(createUserParams)
    .then(res => {
      // login() below dispatches LOGIN_SUCCESS (isAuthenticated: true) as
      // soon as the SDK login call resolves, which drives the post-signup
      // redirect to the dashboard (SignUpPage's <NamedRedirect>) — well
      // before the save two .then() levels deeper is dispatched. The
      // pending-save promise is registered here, synchronously, in the same
      // tick that kicks off login() (not nested inside its .then()), so it
      // exists before that redirect can possibly happen. Registering it
      // any later leaves a window where the dashboard finds nothing to
      // wait for and falsely reports availability as empty.
      const postSignupAvailabilitySave = dispatch(login(email, password)).then(() => {
        // NOTE: addFreeCoachSubscription was previously dispatched here.
        // It assigned every new coach a fake "Network" subscription with
        // a far-future expiryDate (2208988800000), which bypassed the real
        // paid subscription flow entirely. Removed so new coaches start
        // with no subscription and must subscribe through Chargebee like
        // they will in production. Pipedrive sync still runs.
        dispatch(addOrUpdateAPerson());
        dispatch(sendOnboardingWelcomeEmail());
        return dispatch(
          isProvider
            ? saveCalendarAvailabilitySetting(buildDefaultAvailabilityData())
            : saveParticipantCalendarAvailabilitySetting(buildDefaultAvailabilityData())
        );
      });
      setPendingDefaultAvailabilitySave(postSignupAvailabilitySave);
      postSignupAvailabilitySave.catch(e => log.error(e, 'default-availability-save-failed', { email }));
      dispatch(signupSuccess(type));

      // dispatch(createOrUpdateContact({
      //   // email: email,
      //   firstname: firstName,
      //   lastname: lastName,
      //   is_provider: isProvider,
      // }));

      // dispatch(createMauticContact({
      //   email: email,
      //   firstname: firstName,
      //   lastname: lastName,
      //   is_provider: isProvider,
      //   flex_uuid: res?.data?.data?.id?.uuid,
      // }))
    })
    .catch(e => {
      dispatch(signupError(storableError(e)));
      log.error(e, 'signup-failed', {
        email: params.email,
        firstName: params.firstName,
        lastName: params.lastName,
      });
    });
};

/**
 * Signup with an identity provider (Facebook / Google / Apple).
 *
 * Called from the /confirm-signup page after the user confirms (or
 * edits) the email + first/last name that the IdP returned. Posts to
 * the server-side `/api/auth/create-user-with-idp` endpoint, which
 *   (1) creates a Sharetribe user with `currentUser.createWithIdp`,
 *       linking it to the IdP user-id, and
 *   (2) immediately exchanges the IdP token for a Sharetribe auth
 *       cookie via `loginWithIdp`.
 * After the server response we run the same client-side side effects
 * the password-based login() thunk runs: fetchCurrentUser + Pipedrive
 * sync.
 *
 * Note: unlike password login, we do NOT pop the V-Corp auto-auth
 * window here. The existing auto-auth URL embeds the raw access /
 * refresh tokens in the path; with IdP login those tokens live only
 * inside the (httpOnly) Sharetribe cookie, so we can't replicate that
 * URL from the browser without exposing tokens to JS. V-Corp will pick
 * up its own session on first visit. This is a known limitation —
 * cross-domain SSO is a follow-up.
 */
export const signupWithIdp = ({ email, firstName, lastName, isProvider }) => dispatch => {
  dispatch(signupWithIdpRequest());

  return fetch('/api/auth/create-user-with-idp', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, firstName, lastName, isProvider }),
  })
    .then(res => {
      if (!res.ok) {
        return res
          .json()
          .catch(() => ({}))
          .then(body => {
            const err = new Error((body && body.error) || `Signup failed (${res.status})`);
            err.status = res.status;
            err.data = body;
            throw err;
          });
      }
      return res.json();
    })
    .then(() => {
      const type = isProvider ? 'iac' : 'lfc';
      // signupWithIdpSuccess flips isAuthenticated to true synchronously,
      // which drives the post-signup dashboard redirect — before
      // fetchCurrentUser() below even starts, let alone before the save two
      // .then() levels deeper is dispatched. Register the pending-save
      // promise in this same synchronous tick, before dispatching that
      // action, so it exists before the redirect can happen — see the
      // matching comment in signup() above for why this ordering matters.
      const postSignupAvailabilitySave = dispatch(fetchCurrentUser()).then(() => {
        dispatch(addOrUpdateAPerson());
        dispatch(sendOnboardingWelcomeEmail());
        return dispatch(
          isProvider
            ? saveCalendarAvailabilitySetting(buildDefaultAvailabilityData())
            : saveParticipantCalendarAvailabilitySetting(buildDefaultAvailabilityData())
        );
      });
      setPendingDefaultAvailabilitySave(postSignupAvailabilitySave);
      postSignupAvailabilitySave.catch(e => log.error(e, 'default-availability-save-failed', { email }));
      dispatch(signupWithIdpSuccess(type));
      // Bring auth state in sync — the server already set the cookie.
      return postSignupAvailabilitySave;
    })
    .catch(e => {
      dispatch(signupWithIdpError(storableError(e)));
      log.error(e, 'signup-with-idp-failed', { email, firstName, lastName });
    });
};
